import type {
  QuestionnaireItemAnswerOptionWithTranslations,
  QuestionnaireItemWithTranslations,
  QuestionnaireWithTranslations,
} from '@liza/fhir-utils';
import {
  buildScoreObservationTemplate,
  buildTemplateExtractExtension,
  frequencyAnswerOption,
  SEMVER_VERSION_ALGORITHM_EXTENSION,
  symptomItem,
  withEs,
} from './scoredSurvey';

/**
 * AUDIT-C (Alcohol Use Disorders Identification Test — Consumption) — a brief validated screen for
 * risky drinking. Item structure and LOINC coding follow the LOINC AUDIT-C panel (LOINC 72109-2),
 * published here under our own canonical `url` and version, mirroring {@link ./gad7} — they share
 * the scored-survey machinery in {@link ./scoredSurvey}.
 *
 * All three items are required and scored 0–4; the total (0–12) is a raw sum derived server-side via
 * Medplum's SDC `QuestionnaireResponse/$extract` (a contained Observation template carrying the
 * total-score LOINC plus `sdc-questionnaire-templateExtract` annotations). Behavior is verified by
 * `src/__tests__/liveMedplum/auditCExtractScoring.test.ts`. Unlike PHQ-9/GAD-7, each item has its
 * own answer set (frequency, drinks-per-day, and binge-frequency), so the three answer-option lists
 * are defined separately rather than shared.
 *
 * Source: the AUDIT-C FHIR survey JSON attached to the originating issue (English + Spanish), whose
 * item structure and LOINC coding match the LOINC AUDIT-C panel (https://loinc.org/72109-2). The
 * English item text is the standard validated AUDIT-C wording; the inline Spanish translations on
 * the `_`-sibling elements are transcribed from the Spanish source JSON. Because AUDIT-C is a
 * validated instrument, these strings must be reviewed against the official bilingual AUDIT-C before
 * go-live and never free-translated; scoring validity depends on the exact wording. The AUDIT was
 * developed by the World Health Organization (WHO).
 *
 * Each item keeps its full wording inline rather than lifting a shared timeframe into a
 * `description` framing statement (the v3 pattern PHQ-9/GAD-7 use): the three items have no common
 * stem, so there is no single framing statement that fits all of them.
 */

/** AUDIT-C total score. Distinct from the panel LOINC (72109-2) on `Questionnaire.code`. */
export const AUDITC_TOTAL_SCORE_LOINC = '75626-2';

/** Item 68518-0 — drinking frequency. */
const drinkingFrequencyAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  frequencyAnswerOption('0', 0, 'LA6270-8', 'Never', 'Nunca'),
  frequencyAnswerOption('1', 1, 'LA18926-8', 'Monthly or less', 'Una o menos veces al mes'),
  frequencyAnswerOption('2', 2, 'LA18927-6', '2-4 times a month', 'De 2 a 4 veces al mes'),
  frequencyAnswerOption('3', 3, 'LA18928-4', '2-3 times a week', 'De 2 a 3 veces a la semana'),
  frequencyAnswerOption('4', 4, 'LA18929-2', '4 or more times a week', '4 o más veces a la semana'),
];

/** Item 68519-8 — standard drinks on a typical drinking day. */
const drinksPerDayAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  frequencyAnswerOption('0', 0, 'LA15694-5', '1 or 2', '1 ó 2'),
  frequencyAnswerOption('1', 1, 'LA15695-2', '3 or 4', '3 ó 4'),
  frequencyAnswerOption('2', 2, 'LA15696-0', '5 or 6', '5 ó 6'),
  frequencyAnswerOption('3', 3, 'LA15697-8', '7 to 9', '7, 8, o 9'),
  frequencyAnswerOption('4', 4, 'LA15698-6', '10 or more', '10 o más'),
];

/** Item 68520-6 — frequency of six-or-more-drink occasions. */
const bingeFrequencyAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  frequencyAnswerOption('0', 0, 'LA6270-8', 'Never', 'Nunca'),
  frequencyAnswerOption('1', 1, 'LA18933-4', 'Less than monthly', 'Menos de una vez al mes'),
  frequencyAnswerOption('2', 2, 'LA18876-5', 'Monthly', 'Mensualmente'),
  frequencyAnswerOption('3', 3, 'LA18930-0', 'Weekly', 'Semanalmente'),
  frequencyAnswerOption('4', 4, 'LA18931-8', 'Daily or almost daily', 'A diario o casi a diario'),
];

const scoredSymptomItems: QuestionnaireItemWithTranslations[] = [
  symptomItem(
    '/68518-0',
    '68518-0',
    'How often do you have a drink containing alcohol',
    'How often do you have a drink containing alcohol?',
    '¿Con qué frecuencia consume alguna bebida alcohólica?',
    true,
    drinkingFrequencyAnswerOptions,
  ),
  symptomItem(
    '/68519-8',
    '68519-8',
    'How many drinks containing alcohol do you have on a typical day when you are drinking',
    'How many drinks containing alcohol do you have on a typical day when you are drinking?',
    '¿Cuántas consumiciones de bebidas alcohólicas suele realizar en un día de consumo normal?',
    true,
    drinksPerDayAnswerOptions,
  ),
  symptomItem(
    '/68520-6',
    '68520-6',
    'How often do you have six or more drinks on one occasion',
    'How often do you have six or more drinks on one occasion?',
    '¿Con qué frecuencia toma 6 o más bebidas alcohólicas en un solo día?',
    true,
    bingeFrequencyAnswerOptions,
  ),
];

const auditCScoreObservationTemplate = buildScoreObservationTemplate({
  id: 'audit-c-score-tmpl',
  totalScoreCode: AUDITC_TOTAL_SCORE_LOINC,
  display: 'AUDIT-C',
  scoredItems: scoredSymptomItems,
});

export const auditCQuestionnaire: QuestionnaireWithTranslations = {
  resourceType: 'Questionnaire',
  meta: {
    profile: ['http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire'],
  },
  extension: [SEMVER_VERSION_ALGORITHM_EXTENSION, buildTemplateExtractExtension('audit-c-score-tmpl')],
  contained: [auditCScoreObservationTemplate],
  url: 'https://liza.health/fhir/questionnaire/audit-c',
  identifier: [{ system: 'urn:ietf:rfc:3986', value: 'https://liza.health/fhir/questionnaire/audit-c' }],
  version: '1',
  name: 'AUDITC',
  language: 'en',
  title: 'AUDIT-C',
  _title: withEs('Identificación de trastornos por consumo de alcohol – Consumo (AUDIT-C)'),
  status: 'active',
  subjectType: ['Patient'],
  date: '2026-06-26',
  publisher: 'Liza Health',
  jurisdiction: [{ coding: [{ system: 'urn:iso:std:iso:3166', code: 'US' }] }],
  copyright:
    'This material contains content from LOINC® (http://loinc.org). LOINC is copyright © 1995-2024, Regenstrief Institute, Inc. and the LOINC Committee, and is available at no cost under the license at http://loinc.org/license. The Alcohol Use Disorders Identification Test (AUDIT) was developed by the World Health Organization (WHO).',
  code: [
    {
      system: 'http://loinc.org',
      code: '72109-2',
      display: 'Alcohol Use Disorder Identification Test - Consumption [AUDIT-C]',
    },
  ],
  item: scoredSymptomItems,
};
